import { LwButton, LwFormInput, useFormButtons } from 'redesign';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { paths } from 'ogp/paths';
import { useCompanyRelativePath } from 'ogp/hooks';
import { useFormContext } from 'react-hook-form';
import { Box, Divider, styled } from '@mui/material';

export type WorkflowCreateForm = {
  workflowName: string;
};

type WorkflowCreateToolboxProps = {
  onCreateClick: () => void;
  mutationStatus: 'idle' | 'loading' | 'error' | 'success';
  disabled: boolean;
};

export const WorkflowCreateToolbox = ({
  onCreateClick,
  mutationStatus,
  disabled,
}: WorkflowCreateToolboxProps) => {
  const { t } = useTranslation(['web-common', 'ogp-screen-specific']);
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const { submitButtonProps, cancelButtonProps } = useFormButtons(mutationStatus);

  const { control } = useFormContext<WorkflowCreateForm>();
  const onCancelClick = () => navigate(generatePath(paths.Workflows));

  return (
    <Box marginBottom={4}>
      <StyledDivider />
      <StyledToolbox>
        <ButtonWrapper>
          <LwButton
            iconColorMode="stroke"
            onClick={onCreateClick}
            {...submitButtonProps}
            disabled={disabled}
          >
            {t('buttons.create', { ns: 'web-common' })}
          </LwButton>
          <LwButton iconColorMode="fill" onClick={onCancelClick} {...cancelButtonProps}>
            {t('buttons.cancel', { ns: 'web-common' })}
          </LwButton>
        </ButtonWrapper>

        <InputWrapper>
          <LwFormInput
            name="workflowName"
            control={control}
            label={'Workflow name'}
            rules={{ required: 'Voer een workflow naam in' }}
          />
        </InputWrapper>
      </StyledToolbox>
    </Box>
  );
};

const StyledToolbox = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(8),
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(4),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  backgroundColor: theme.palette.lwPrimaryAccentDark[20],
}));

const ButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const InputWrapper = styled(Box)({
  display: 'flex',
});
